import React from 'react';
import { Navigate } from 'react-router-dom';

// roles: string or array of allowed roles, e.g. 'admin' or ['admin', 'authorizer']
// assignmentRolePrefix: gate by `user.assignments` membership instead of the
// single `user.role` winner - needed for middle_office_* tiers, since a user
// can hold a middle-office assignment ALONGSIDE a deal-workflow role (e.g.
// back_office_verifier), which would otherwise win `effectiveRole` and hide
// middle-office-only pages. See utils/effectiveWorkflowAuth.js ROLE_PRIORITY
// (backend) for the same reasoning.
// Shown when a signed-in user reaches a page they lack rights for. Sending
// them to /login instead (as this used to) is misleading - they ARE logged in,
// and being dropped on the login screen looks like the session broke.
function AccessDenied({ reason }) {
  return (
    <div className="sister-blotter" style={{ padding: 24 }}>
      <h2 style={{ marginTop: 0 }}>Access denied</h2>
      <p style={{ color: '#666' }}>{reason}</p>
      <p style={{ color: '#666' }}>
        You are still signed in. Contact an administrator if you need access to this page.
      </p>
    </div>
  );
}

export default function ProtectedRoute({ children, roles, allowedTabs, tabKey, assignmentRolePrefix }) {
  const user = JSON.parse(localStorage.getItem('user'));
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const isAdmin = user.role === 'admin';

  if (assignmentRolePrefix) {
    const hasAssignment = Array.isArray(user.assignments) && user.assignments.some(
      (a) => typeof a.role === 'string' && a.role.startsWith(assignmentRolePrefix)
    );
    if (!isAdmin && !hasAssignment) {
      return (
        <AccessDenied
          reason={
            assignmentRolePrefix === 'middle_office_'
              ? 'This page is restricted to Middle Office users. Your account has no Middle Office assignment.'
              : 'Your account does not hold the assignment required for this page.'
          }
        />
      );
    }
    return children;
  }

  if (roles) {
    const allowed = Array.isArray(roles) ? roles : [roles];
    if (!allowed.includes(user.role)) {
      // Redirect to first allowed tab if not authorized for this route
      const ALL_TABS = [
        { key: 'transactions', path: '/transactions' },
        { key: 'isin_master', path: '/isin-master' },
        { key: 'counterparty_master', path: '/counterparty-master' },
        { key: 'limit_setup', path: '/limit-setup' },
        { key: 'fixed_income_gsec', path: '/fixed-income-gsec' },
        { key: 'fixed_income_others', path: '/fixed-income-others' },
        { key: 'payments_rtgs', path: '/payments-rtgs' },
        { key: 'payments_other', path: '/payments-other' },
        { key: 'repo', path: '/repo' },
        { key: 'reverse_repo', path: '/mark-to-market' },
      ];
      let allowedTabs = user.allowed_tabs || [];
      if (user.role === 'limits_allocating_user' || user.role === 'limits_allocating_authorizer') {
        allowedTabs = ['limit_setup'];
      }
      const firstTab = ALL_TABS.find(tab => allowedTabs.includes(tab.key));
      if (firstTab) {
        return <Navigate to={firstTab.path} replace />;
      }
      // fallback to login
      return <Navigate to="/login" replace />;
    }
  }

  // Tab-based permission gate (used throughout App.js)
  if (tabKey && !isAdmin) {
    const allowedTabKeysFromProp = Array.isArray(allowedTabs) ? allowedTabs.map(t => t.key) : null;
    const allowedKeys = allowedTabKeysFromProp || (user.allowed_tabs || []);
    if (!allowedKeys.includes(tabKey)) {
      const redirectPath =
        Array.isArray(allowedTabs) && allowedTabs.length > 0
          ? allowedTabs[0].path
          : '/login';
      return <Navigate to={redirectPath} replace />;
    }
  }

  return children;
}
